import { Box, CircularProgress, Typography, useTheme, Button, Stack } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'
import { useParams } from 'react-router-dom'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context'
import { observer } from 'mobx-react'

const ReconnectHandler = observer( () => {
    const { meeting_id } = useParams()
    const { palette } = useTheme()

    // Retry/backoff is handled centrally inside safeRejoinMeeting itself (it's
    // called from several places), so this component just triggers it and
    // reflects the shared retry state for the UI.
    const { safeRejoinMeeting, leaveChannel, isRejoining, rejoinGaveUp } = useMeetingActionsContext()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

    const isDisconnected = OnlineSessionSocketStore.isDisconnected
    const isConnecting = OnlineSessionSocketStore.isConnecting

    const gaveUp = rejoinGaveUp

    // Avoid triggering a second rejoin while one is already in flight
    const triggeredRef = useRef( false )

    useEffect( () => {
        if ( isDisconnected && !isRejoining && !gaveUp && !triggeredRef.current ) {
            triggeredRef.current = true
            safeRejoinMeeting().catch( () => {} ).finally( () => {
                triggeredRef.current = false
            } )
        }

        if ( !isDisconnected ) {
            triggeredRef.current = false
        }
    }, [isDisconnected, isRejoining, gaveUp, safeRejoinMeeting] )

    if ( !isDisconnected && !gaveUp && !isConnecting ) return null

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor={palette.background.paper + "E6"}
            position="fixed"
            sx={{ inset: 0, zIndex: 9999, backdropFilter: 'blur(4px)' }}
        >
            <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center', p: 4 }}>
                {gaveUp ? (
                    <>
                        <Typography variant="h5" color="error" fontWeight="bold">
                            Connection Lost
                        </Typography>
                        <Typography color="textSecondary">
                            We tried to reconnect several times but couldn't reach the server.
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button variant="contained" color="error" onClick={leaveChannel}>
                                Exit Meeting
                            </Button>
                        </Stack>
                    </>
                ) : ""}

                {!gaveUp && (
                    <>
                        <CircularProgress size={40} thickness={4} />
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Connection Interrupted
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Restoring your session...
                            </Typography>
                        </Box>
                    </>
                )}
            </Stack>
        </Box>
    )
} )

export default ReconnectHandler